import { createSelector } from '@reduxjs/toolkit';

import { countTotalBet, getLastBetType } from '../../../utils/bets';
import { CoreRootState, useCoreSelector } from '../../CoreStore';
import { selectHasTotalBet, selectLastBet, selectPlacedBetType, selectTotalBet } from '../bets/selectors';
import type { BetType } from '../bets/types';
import { selectFreeChipBalance } from '../bonusPromotions/selectors';
import { ChipTypes } from '../chips/types';
import { selectCurrencyDecimals, selectDeductibleBalance } from '../game/selectors';

import { AutoplayLimits, AutoplayStatus } from './types';

export const selectTotalRounds = ({ autoplay }: CoreRootState): number => autoplay.totalRounds;
const selectIsInProgress = ({ autoplay }: CoreRootState): boolean => autoplay.isInProgress;
const selectAutoplayStatus = ({ autoplay }: CoreRootState): AutoplayStatus => autoplay.autoplayStatus;
const selectIsOpen = ({ autoplay }: CoreRootState): boolean => autoplay.isOpen;
const selectRemainingCount = ({ autoplay }: CoreRootState): number => autoplay.roundsLeft;
const selectSavedBet = ({ autoplay }: CoreRootState): BetType | null => autoplay.savedBet;
const selectIsLaunchedMidRound = ({ autoplay }: CoreRootState): boolean => autoplay.isLaunchedMidRound;
const selectSingleWinLimit = ({ autoplay }: CoreRootState): number | null => autoplay.singleWinLimit;
const selectBalanceDecreaseLimit = ({ autoplay }: CoreRootState): number | null => autoplay.balanceDecreaseLimit;
const selectBalanceIncreaseLimit = ({ autoplay }: CoreRootState): number | null => autoplay.balanceIncreaseLimit;
const selectHasPendingAutoplayBet = ({ autoplay }: CoreRootState): boolean => autoplay.pendingAutoplayBet;
const selectActiveBetAmount = ({ autoplay }: CoreRootState): number => autoplay.activeBetAmount;

const selectIsLowBalance = (state: CoreRootState): boolean => {
  const lastBet = selectLastBet(state);
  const lastBetType = getLastBetType(lastBet);
  const balance = selectDeductibleBalance(state);
  const freeChipBalance = selectFreeChipBalance(state);
  const isBetPlaced = selectHasTotalBet(state); // no
  const currentBetType = selectPlacedBetType(state); // null
  const totalBet = selectCurrentBetAmount(state); // 0

  const relevantBetType = isBetPlaced && currentBetType ? currentBetType : lastBetType;
  const relevantBalance = relevantBetType === ChipTypes.CasinoChip ? balance : freeChipBalance;

  return relevantBalance < totalBet;
};

export const selectCurrentBetAmount = createSelector(
  [selectTotalBet, selectLastBet, selectCurrencyDecimals, selectActiveBetAmount, selectAutoplayStatus],
  (currentBetAmount, previousBet, decimals, activeBetAmount, autoplayStatus) => {
    const isAutoplayRunning = autoplayStatus !== AutoplayStatus.Stopped;
    if (isAutoplayRunning) {
      return activeBetAmount;
    }

    const hasCurrentBet = currentBetAmount > 0;
    if (hasCurrentBet) {
      return currentBetAmount;
    }

    const previousBetAmount = previousBet ? countTotalBet(previousBet.betSpots, decimals) : 0;
    return previousBetAmount;
  }
);

const selectAutoplayLimits = createSelector(
  [selectSingleWinLimit, selectBalanceDecreaseLimit, selectBalanceIncreaseLimit],
  (singleWinLimit, balanceDecreaseLimit, balanceIncreaseLimit) => ({
    singleWinLimit,
    balanceDecreaseLimit,
    balanceIncreaseLimit,
  })
);

export const useAutoplaySlice = {
  isInProgress: (): boolean => {
    return useCoreSelector(selectIsInProgress);
  },
  getAutoplayStatus: (): AutoplayStatus => {
    return useCoreSelector(selectAutoplayStatus);
  },
  isOpen: (): boolean => {
    return useCoreSelector(selectIsOpen);
  },
  getRemainingCount: (): number => {
    return useCoreSelector(selectRemainingCount);
  },
  getTotalRounds: (): number => {
    return useCoreSelector(selectTotalRounds);
  },
  getSavedBet: (): BetType | null => {
    return useCoreSelector(selectSavedBet);
  },
  isLaunchedMidRound: (): boolean => {
    return useCoreSelector(selectIsLaunchedMidRound);
  },
  getAutoplayLimits: (): AutoplayLimits => {
    return useCoreSelector(selectAutoplayLimits);
  },
  hasPendingAutoplayBet: (): boolean => {
    return useCoreSelector(selectHasPendingAutoplayBet);
  },
  getAutoplayOnboarded: (): boolean => {
    return useCoreSelector(({ autoplay }) => autoplay.isOnboarded);
  },
  getAutoplayRoundsOptions: (): number[] => {
    return useCoreSelector(({ autoplay }) => autoplay.roundsOptions);
  },
  getAutoplayRoundsAddons: (): number[] => {
    return useCoreSelector(({ autoplay }) => autoplay.roundsAddons);
  },
  getAutoplayMaxRoundsLimit: (): number => {
    return useCoreSelector(({ autoplay }) => autoplay.maxRoundsLimit);
  },
  getShowIndicator: (): boolean => {
    return useCoreSelector(({ autoplay }) => autoplay.showIndicator);
  },
  /**
   * Gets the amount with which autoplay is currently running.
   * This is the actual bet amount being used during autoplay execution.
   */
  getActiveBetAmount: (): number => {
    return useCoreSelector(selectActiveBetAmount);
  },
  /**
   * Gets the bet amount shown in the autoplay UI.
   * This value may differ from the active autoplay bet (e.g., before autoplay starts or when user is configuring it).
   */
  getCurrentBetAmount: (): number => {
    return useCoreSelector(selectCurrentBetAmount);
  },
  /**
   * Returns true if the user's balance is too low for the current bet or last bet, based on chip type.
   */
  isLowBalance: (): boolean => {
    return useCoreSelector(selectIsLowBalance);
  },
  // TODO: rework to not use any external dependencies
  isDisableAutoPlayForFreeChip: (remainingFreeChips: number): boolean => {
    return useCoreSelector(({ autoplay }) => {
      if (autoplay.savedBet) {
        const sumOfFreeChipCount = Object.values(autoplay.savedBet.betSpots).reduce(
          (sum, betSpot) =>
            betSpot.value > 0 && betSpot.type === ChipTypes.FreeChip ? sum + (betSpot.count || 0) : sum,
          0
        );
        return sumOfFreeChipCount > remainingFreeChips;
      }
      return false;
    });
  },
};
